/** @format */

import React, { useContext } from 'react'
import styled from 'styled-components'
import { Descriptions, DescriptionItem, Tag } from '@king-design/react'
import { AlarmCallBackNew, AlarmReciverNew } from 'src/components'
import {
  TriggerTypeEnum,
  ActionTypeEnum,
  TriggerDetailItemProp,
  NoticeTypeEnum,
  getCronSpecValue
} from '../../container'
import TriggerCodeMirror from './TriggerCodeMirror'
import { CountContext } from '../..'
import { TriggerRuleKeyAggEnum, TriggerRuleKeyEnum } from './container'
import { EventItemProps } from 'src/pages/EventCenter/constants'
import { UserItemProps, GroupItemProps } from 'src/components/AlarmReciver/new'
import useGlobalData from 'src/hooks/useGlobalData'
import { RegionStateType } from 'src/store/globalDataSlice'

interface ReadonlyProps {
  className?: string
  record: TriggerDetailItemProp
  sceneInput: string[]
  eventResult?: EventItemProps[]
  reciverResult?: {
    userlist?: UserItemProps[]
    grouplist?: GroupItemProps[]
  }
}

const Readonly: React.FC<ReadonlyProps> = ({
  className,
  record,
  sceneInput,
  eventResult: eventResultProps,
  reciverResult: reciverResultProps
}) => {
  const context = useContext(CountContext)
  const eventResult = eventResultProps || context.eventResult || []
  const reciverResult = reciverResultProps ||
    context.reciverResult || { userlist: [], grouplist: [] }
  const regionResult = useGlobalData('monitorRegionList') as RegionStateType['list']

  const renderTriggerConditions = conditions => {
    return (
      <div className="trigger_conditions">
        {conditions?.map((condition, orIndex) => {
          const filterProduct: EventItemProps[] = eventResult.filter(
            event => event.productType === condition[0].Value
          )
          return (
            <div className="trigger_conditions_item" key={orIndex}>
              <div className="trigger_conditions_item_header">
                <span>#{orIndex + 1}.</span>
              </div>
              <div className="trigger_conditions_item_content">
                {condition?.map(item => {
                  let findValue: string = item.Value
                  if (item.Key === 'region_key') {
                    findValue = item.Value?.map(
                      region => regionResult.find(item => item.regionKey === region)?.regionName
                    ).join('、')
                  } else if (item.Key === 'namespaceid') {
                    findValue = filterProduct?.[0]?.productName
                  } else if (item.Key === 'event_type') {
                    findValue = item.Value?.map(value => {
                      return filterProduct.find(event => event.eventType === value)?.eventTypeDesc
                    }).join('，')
                  } else if (item.Key === 'event_name') {
                    findValue = Array.isArray(item.Value)
                      ? item.Value?.map(value => {
                          return filterProduct.find(event => event.name === value)?.nameDesc
                        }).join('，')
                      : filterProduct.find(event => event.name === item.Value)?.nameDesc
                  } else if (item.Key === 'event_level') {
                    findValue = item.Value?.map(value => {
                      return filterProduct.find(event => event.eventLevel === value)?.eventLevel
                    }).join('，')
                  } else if (item.Key === 'event_status') {
                    findValue = item.Value?.map(value => {
                      return filterProduct.find(event => event.eventStatus === value)?.eventStatus
                    }).join('，')
                  }
                  return (
                    <div className="trigger_conditions_item_content_item" key={item.Key}>
                      <p>
                        {record?.TriggerType === 'monitor_event_agg'
                          ? TriggerRuleKeyAggEnum[item.Key]
                          : TriggerRuleKeyEnum[item.Key]}
                      </p>
                      <p>{item.Operator}</p>
                      <p>{findValue}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (!record) return null

  const cronSpecValue = getCronSpecValue(record?.TriggerConditions?.CronSpec)
  return (
    <Descriptions columns={1} className={className}>
      <DescriptionItem label="规则名称">{record?.Name || '-'}</DescriptionItem>
      <DescriptionItem label="触发源">
        {record?.TriggerType ? TriggerTypeEnum[record?.TriggerType] : '-'}
      </DescriptionItem>
      <DescriptionItem label="触发规则">
        {record?.TriggerType === 'crontab'
          ? `${cronSpecValue?.minute}分 ${cronSpecValue?.hour}时 ${cronSpecValue?.dom}日 ${cronSpecValue?.month}月 ${cronSpecValue?.dow}周`
          : renderTriggerConditions(record?.TriggerConditions?.Conditions)}
      </DescriptionItem>
      <DescriptionItem label="触发动作">
        {record?.ActionType ? ActionTypeEnum[record?.ActionType] : '-'}
      </DescriptionItem>
      {record?.ActionType === 'location' ? (
        <DescriptionItem label="场景名称">
          <>
            <p>{record?.ActionName || '-'}</p>
            <div className="form_item_extra">
              <span>触发场景所需参数：</span>
              {sceneInput?.length ? (
                <>
                  <span>
                    {sceneInput.map(item => {
                      return (
                        <Tag
                          key={item}
                          border="solid"
                          type={record.ActionParams?.Outputs?.includes(item) ? 'success' : 'danger'}
                        >
                          {item}
                        </Tag>
                      )
                    })}
                  </span>
                  <span>。请在下方数据处理中，使用g_print函数输出所需参数。</span>
                </>
              ) : (
                '无'
              )}
            </div>
          </>
        </DescriptionItem>
      ) : null}
      <DescriptionItem label="数据处理">
        <div style={{ width: 650 }}>
          <TriggerCodeMirror readOnly scriptID={record?.ActionParams?.ScriptID} />
          <p className="form_item_extra">请在数据转换中将原始参数转换为目标动作所需参数</p>
        </div>
      </DescriptionItem>
      <DescriptionItem label="通知订阅" className="notice_item">
        <div className="ksc-space">
          {record?.Notice?.Events?.map(item => {
            return (
              <Tag key={item} border="solid">
                {NoticeTypeEnum[item]}
              </Tag>
            )
          })}
        </div>
      </DescriptionItem>
      <DescriptionItem label="通知联系人">
        <AlarmReciverNew
          value={record?.Notice?.UserNotices}
          userlist={reciverResult?.userlist}
          grouplist={reciverResult?.grouplist}
          readonly
        />
      </DescriptionItem>
      <DescriptionItem label="通知回调">
        <AlarmCallBackNew value={record?.Notice?.UrlNotices} readonly />
      </DescriptionItem>
      <DescriptionItem label="熔断机制" className="limiter_item">
        <>
          <p className="limiter_item_text">
            滑动窗口<b>{record?.Limiter.Duration}</b>秒内, 最多触发
            <b>{record?.Limiter.Max}</b>次
          </p>
          <p className="form_item_extra">
            滑动窗口期内本触发器触发动作超过设置次数时将开启熔断。任意值为0时，不会触发熔断机制。
          </p>
        </>
      </DescriptionItem>
    </Descriptions>
  )
}

export default styled(Readonly)`
  .k-descriptions-content {
    padding-top: 0;
    width: 752px;
    .k-description-item {
      align-items: flex-start;
      .k-description-item-label {
        width: 78px;
      }
      .k-description-centent {
        width: 650px;
        max-width: 100%;
      }
      &.notice_item {
        .k-description-item-label {
          height: 20px;
          line-height: 20px;
        }
      }
      &.limiter_item {
        .limiter_item_text {
          b {
            margin: 0 8px;
          }
        }
      }
    }
  }
  .trigger_conditions {
    .trigger_conditions_item {
      border: 1px solid #f0f0f0;
      border-radius: 2px;
      margin-bottom: 12px;
      .trigger_conditions_item_header {
        padding: 0 12px;
        height: 32px;
        line-height: 32px;
        border-bottom: 1px solid #f0f0f0;
      }
      .trigger_conditions_item_content {
        padding: 8px 12px;
        .trigger_conditions_item_content_item {
          display: flex;
          align-items: flex-start;
          p {
            margin-bottom: 0;
            &:nth-child(1) {
              width: 60px;
            }
            &:nth-child(2) {
              width: 20px;
            }
            &:nth-child(3) {
              width: 544px;
              word-break: break-all;
              white-space: normal;
            }
            &:not(:last-child) {
              margin-right: 4px;
            }
          }
          &:not(:last-child) {
            margin-bottom: 4px;
          }
        }
      }
    }
  }
`
